// Action Sheet PDF
// ----------------
// Builds the shelter "Action Sheet" (per-child instructions for a request's
// session) as a TRUE PDF using jsPDF (window.jspdf, loaded via <script> in
// index.html — same global authorityDoc.ts uses).
//
// Why a real PDF instead of window.print(): printing the live app page made the
// browser stamp its OWN header/footer (page title, URL, page numbers, timestamp)
// onto the sheet, and whether that appeared depended on the printer's hardware
// margins (Firefox draws the header in whatever margin space exists). A generated
// PDF has no browser chrome on any printer or browser — it looks identical
// everywhere. The PDF opens in a new tab (with a download fallback if the popup
// is blocked) so the user can print, save, or email it.

import { BASE_PT, htmlToRuns, PARA_GAP_RATIO, type RichLine } from "../services/richText";
import { money } from "./helpers";
import { logger } from "./logger";

export interface ActionSheetItem {
  status: string;
  type: string;
  amount?: string | number | null;
  message: string;
}

const PT_TO_MM = 0.352778;

interface WrapToken {
  text: string;
  isSpace: boolean;
  bold: boolean;
  underline: boolean;
  sizePt: number;
  w: number;
}

// Lay out (and optionally draw) rich runs with word-wrap; returns the height in
// mm. The message may be the sanitised HTML subset (b / u / span[font-size] / br)
// or plain text (one Normal run). jsPDF places the text baseline, so we derive a
// baseline per wrapped visual line from that line's tallest run.
// (Exported for unit tests — production callers stay within this module.)
export function drawRichText(
  doc: JsPdfDoc,
  lines: RichLine[],
  x0: number,
  yTop: number,
  maxWidth: number,
  draw: boolean
): number {
  const LEAD = 1.32; // line spacing factor
  const ASCENT = 0.82; // baseline offset down from the top of the line
  let y = yTop;

  lines.forEach(function (line) {
    // Tokenise into words + spaces, each carrying its run's style + measured width.
    const tokens: WrapToken[] = [];
    line.forEach(function (run) {
      run.text.split(/(\s+)/).forEach(function (part) {
        if (part === "") return;
        doc.setFont("helvetica", run.bold ? "bold" : "normal");
        doc.setFontSize(run.sizePt);
        tokens.push({
          text: part,
          isSpace: /^\s+$/.test(part),
          bold: run.bold,
          underline: run.underline,
          sizePt: run.sizePt,
          w: doc.getTextWidth(part),
        });
      });
    });

    if (tokens.length === 0) {
      // Paragraph gap: a blank line advances at PARA_GAP_RATIO of a full line,
      // matching the screen render (richTextDisplayHtml) — one shared knob.
      y += BASE_PT * PT_TO_MM * LEAD * PARA_GAP_RATIO;
      return;
    }

    // Greedy word-wrap into visual lines.
    const visual: WrapToken[][] = [];
    let cur: WrapToken[] = [];
    let curW = 0;
    tokens.forEach(function (tok) {
      if (!tok.isSpace && curW + tok.w > maxWidth && cur.length > 0) {
        visual.push(cur);
        cur = [];
        curW = 0;
      }
      if (tok.isSpace && cur.length === 0) return; // no leading space after a wrap
      cur.push(tok);
      curW += tok.w;
    });
    if (cur.length > 0) visual.push(cur);

    visual.forEach(function (vl) {
      const maxPt = vl.reduce<number>(function (m, t) {
        return Math.max(m, t.sizePt);
      }, BASE_PT);
      const baseline = y + maxPt * PT_TO_MM * ASCENT;
      if (draw) {
        let x = x0;
        vl.forEach(function (tok) {
          doc.setFont("helvetica", tok.bold ? "bold" : "normal");
          doc.setFontSize(tok.sizePt);
          doc.text(tok.text, x, baseline);
          if (tok.underline && !tok.isSpace) {
            doc.setLineWidth(0.25);
            doc.line(x, baseline + 0.6, x + tok.w, baseline + 0.6);
          }
          x += tok.w;
        });
      }
      y += maxPt * PT_TO_MM * LEAD;
    });
  });

  return y - yTop;
}

export interface ActionSheetData {
  refNo: string;
  shelterRef: string;
  contact: string;
  phone: string;
  delivery: string;
  date: string;
  childDesc: string;
  items: ActionSheetItem[];
}

type RGB = [number, number, number];

// jsPDF loads async from a CDN <script>; poll briefly (up to ~5s) for it.
function waitForJsPDF(): Promise<boolean> {
  return new Promise(function (resolve) {
    var attempts = 0;
    var check = function () {
      if (window.jspdf?.jsPDF) return resolve(true);
      if (++attempts > 50) return resolve(false);
      setTimeout(check, 100);
    };
    check();
  });
}

export async function openActionSheetPdf(data: ActionSheetData): Promise<boolean> {
  var ready = await waitForJsPDF();
  if (!ready) {
    logger.warn("jsPDF not loaded — cannot build Action Sheet PDF");
    return false;
  }

  var jsPDF = window.jspdf!.jsPDF;
  var doc = new jsPDF({ unit: "mm", format: "a4" });

  var pageW = 210;
  var margin = 18;
  var contentW = pageW - margin * 2;
  var y = 18;

  var teal: RGB = [0, 105, 92];
  var dark: RGB = [26, 46, 43];
  var muted: RGB = [96, 125, 121];
  var body: RGB = [55, 71, 79];

  function checkPage(needed: number) {
    if (y + needed > 282) {
      doc.addPage();
      y = 18;
    }
  }

  // ── Title ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(teal[0], teal[1], teal[2]);
  doc.text("Zephyr Education — Action Sheet", margin, y);
  y += 8;

  // ── Meta (two columns of label: value) ──
  doc.setFontSize(9.5);
  var colGap = contentW / 2;
  var metaTop = y;

  function metaCol(rows: Array<[string, string]>, x: number): number {
    var yy = metaTop;
    rows.forEach(function (r) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(dark[0], dark[1], dark[2]);
      doc.text(r[0], x, yy);
      var labelW = doc.getTextWidth(r[0]) + 1.5;
      doc.setFont("helvetica", "normal");
      var valLines = doc.splitTextToSize(String(r[1] || ""), colGap - labelW - 4);
      doc.text(valLines, x + labelW, yy);
      yy += Math.max(5, valLines.length * 5);
    });
    return yy;
  }

  var leftEnd = metaCol(
    [
      ["Request:", data.refNo],
      ["DFV Service Ref:", data.shelterRef],
      ["Contact:", data.contact],
    ],
    margin
  );
  var rightEnd = metaCol(
    [
      ["Phone:", data.phone],
      ["Delivery:", data.delivery],
      ["Date:", data.date],
    ],
    margin + colGap
  );
  y = Math.max(leftEnd, rightEnd) + 2;

  // ── Divider ──
  doc.setDrawColor(teal[0], teal[1], teal[2]);
  doc.setLineWidth(0.6);
  doc.line(margin, y, margin + contentW, y);
  y += 7;

  // ── Child line ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(teal[0], teal[1], teal[2]);
  var childLines = doc.splitTextToSize(data.childDesc, contentW);
  doc.text(childLines, margin, y);
  y += childLines.length * 5 + 4;

  // ── Item cards ──
  if (!data.items || data.items.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(muted[0], muted[1], muted[2]);
    doc.text("No items in this session for this child.", margin, y);
  } else {
    data.items.forEach(function (item) {
      var badge =
        item.status === "Approved"
          ? { fill: [232, 245, 233] as RGB, ink: [46, 125, 50] as RGB }
          : item.status === "Declined"
            ? { fill: [255, 235, 238] as RGB, ink: [198, 40, 40] as RGB }
            : { fill: [255, 248, 225] as RGB, ink: [245, 127, 23] as RGB };

      const msgRuns = htmlToRuns(item.message || "");
      const msgH = drawRichText(doc, msgRuns, margin + 4, 0, contentW - 8, false);
      const cardH = 14 + msgH + 3;
      checkPage(cardH + 4);

      // Card background
      doc.setFillColor(249, 251, 249);
      doc.setDrawColor(207, 224, 222);
      doc.setLineWidth(0.3);
      doc.rect(margin, y, contentW, cardH, "FD");

      // Status badge
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      var badgeW = doc.getTextWidth(item.status) + 6;
      doc.setFillColor(badge.fill[0], badge.fill[1], badge.fill[2]);
      doc.roundedRect(margin + 4, y + 4, badgeW, 6, 1, 1, "F");
      doc.setTextColor(badge.ink[0], badge.ink[1], badge.ink[2]);
      doc.text(item.status, margin + 4 + 3, y + 8.2);

      // Type + amount
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(dark[0], dark[1], dark[2]);
      doc.text(item.type, margin + 4 + badgeW + 4, y + 8.6);
      if (item.amount) {
        var amt = parseFloat(String(item.amount));
        if (!Number.isNaN(amt)) {
          doc.setTextColor(56, 142, 60);
          doc.text(money(amt), margin + contentW - 4, y + 8.6, { align: "right" });
        }
      }

      // Message (rich: bold / underline / font-size, or plain text)
      doc.setTextColor(body[0], body[1], body[2]);
      doc.setDrawColor(body[0], body[1], body[2]);
      drawRichText(doc, msgRuns, margin + 4, y + 14, contentW - 8, true);

      y += cardH + 4;
    });
  }

  // ── Output: open in a new tab, fall back to download if the popup is blocked ──
  try {
    var blob = doc.output("blob");
    var url = URL.createObjectURL(blob);
    var win = window.open(url, "_blank");
    if (!win) {
      doc.save(`Action Sheet - ${data.refNo}.pdf`);
      URL.revokeObjectURL(url);
    } else {
      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 60000);
    }
    return true;
  } catch (e) {
    logger.warn("Action Sheet PDF open failed:", e);
    return false;
  }
}
